"use client";

import React, { useEffect, useState } from "react";
import { Loader, Tag, Folder, ShoppingBag, CircleAlert } from "lucide-react";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleProductConsumer() {
  const { products, loading, error } = useEcommerceProducts();

  if (loading) {
    return (
      <div data-section-id="853" className="flex min-h-[400px] w-full items-center justify-center">
        <Loader className="text-primary h-10 w-10 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[400px] w-full flex-col items-center justify-center gap-4 text-center">
        <CircleAlert className="text-destructive h-12 w-12" />
        <p className="text-muted-foreground font-sans text-lg">Unable to load the requested resource at this time.</p>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="flex min-h-[400px] w-full flex-col items-center justify-center gap-4 text-center">
        <ShoppingBag className="text-muted-foreground h-12 w-12 opacity-50" />
        <p className="text-muted-foreground font-sans text-lg">We couldn't find the guide you're looking for.</p>
      </div>
    );
  }

  const product = products[0];

  return (
    <div className="container mx-auto px-4 py-12 md:py-20">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-20">
        {}
        <div className="relative">
          <div className="bg-primary/10 absolute -inset-4 -z-10 rounded-3xl blur-2xl" />
          <AspectRatio ratio={1} className="bg-card border-border/50 overflow-hidden rounded-2xl border shadow-sm">
            {product.featured_image_url ? (
              <img
                src={product.featured_image_url}
                alt={product.title}
                className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
              />
            ) : (
              <div className="text-muted-foreground flex h-full w-full flex-col items-center justify-center bg-muted/30">
                <ShoppingBag className="mb-4 h-16 w-16 opacity-20" />
                <span className="font-sans">No preview available</span>
              </div>
            )}
          </AspectRatio>
        </div>

        {}
        <div className="flex flex-col justify-center space-y-8">
          <div>
            <h1 className="text-foreground font-serif mb-4 text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
              {product.title}
            </h1>

            <div className="flex items-baseline gap-4">
              {product.discounted_price ? (
                <>
                  <span className="text-primary font-sans text-4xl font-bold">
                    ${product.discounted_price.toLocaleString()}
                  </span>
                  <span className="text-muted-foreground font-sans text-xl line-through decoration-destructive/50 decoration-2">
                    ${product.price.toLocaleString()}
                  </span>
                  <Badge className="bg-destructive text-destructive-foreground text-sm font-sans">
                    Special Offer
                  </Badge>
                </>
              ) : (
                <span className="text-primary font-sans text-4xl font-bold">
                  ${product.price?.toLocaleString() || "0"}
                </span>
              )}
            </div>
          </div>

          {product.short_description && (
            <div
              className="prose prose-base text-muted-foreground font-sans border-primary/20 border-l-4 pl-6"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          <div className="border-border/50 border-t pt-8">
              <AddToCartButton
                productId={product.id}
                showQuantity={true}
                text="Add to Basket"
                className="w-full max-w-md"
                buttonClassName="bg-destructive text-destructive-foreground hover:bg-destructive/90 h-12 text-lg shadow-lg shadow-destructive/10 w-full rounded-full font-sans transition-all"
              />
            </div>

          <div className="space-y-4 pt-4">
            {product.categories && product.categories.length > 0 && (
              <div className="flex flex-wrap items-start gap-2 text-sm">
                <span className="text-foreground flex min-w-24 items-center gap-2 font-semibold font-serif">
                  <Folder className="text-primary h-4 w-4" />
                  Category:
                </span>
                <div className="flex flex-wrap gap-2">
                  {product.categories.map((cat, i) => (
                    <Link
                      key={cat.id}
                      data-index={i}
                      to={cat.link}
                      className="text-muted-foreground font-sans hover:text-primary transition-colors hover:underline"
                    >
                      {cat.name}
                      {i < product.categories.length - 1 ? "," : ""}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {product.tags && product.tags.length > 0 && (
              <div className="flex flex-wrap items-start gap-2 text-sm">
                <span className="text-foreground flex min-w-24 items-center gap-2 font-semibold font-serif">
                  <Tag className="text-primary h-4 w-4" />
                  Resource Tags:
                </span>
                <div className="flex flex-wrap gap-2">
                  {product.tags.map((tag, i) => (
                    <Link
                      key={tag.id}
                      data-index={i}
                      to={tag.link}
                      className="text-muted-foreground font-sans hover:text-primary transition-colors hover:underline"
                    >
                      {tag.name}
                      {i < product.tags.length - 1 ? "," : ""}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {}
      {product.long_description && (
        <div className="mt-20">
          <div className="mb-10 flex items-center gap-4">
            <div className="bg-border h-px flex-1"></div>
            <h2 className="text-foreground font-serif text-center text-2xl font-bold md:text-3xl">
              Resource Details
            </h2>
            <div className="bg-border h-px flex-1"></div>
          </div>
          <div className="mx-auto max-w-4xl">
            <div
              className="prose prose-lg text-muted-foreground font-sans bg-card border-border/50 max-w-none rounded-3xl border p-8 shadow-sm md:p-12"
              dangerouslySetInnerHTML={{ __html: product.long_description }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function SingleProductTemplateSection() {
  const [query, setQuery] = useState<any>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const postId = window.WVC && window.WVC.postId ? parseInt(window.WVC.postId) : null;
      if (postId) {
        setQuery({ include: [postId], per_page: 1 });
      } else if (window.wvcClient && typeof window.wvcClient.getCurrentWPQuery === "function") {
        const currentQuery = window.wvcClient.getCurrentWPQuery();
        setQuery(currentQuery);
      }
    }
  }, []);

  if (!query) {
    return (
      <section
        id="single-product-template-section"
        className="bg-background flex min-h-[60vh] items-center justify-center"
      >
        <Loader className="text-muted-foreground h-8 w-8 animate-spin" />
      </section>
    );
  }

  return (
    <section id="single-product-template-section" className="bg-background text-foreground">
      <EcommerceProductsProvider postType="product" wp_query={query}>
        <SingleProductConsumer />
      </EcommerceProductsProvider>
    </section>
  );
}